import { db } from './dexie'
import { supabase, isSupabaseConfigured, supabaseRestUrl, supabaseApiKey } from '../lib/supabase'
import { logger } from '../lib/logger'
import { captureError } from '../lib/errorReporting'
import { locallyModifiedNoteIds } from '../hooks/useNotes'

const log = logger('Sync')

export type SyncStatus = 'idle' | 'unsynced' | 'syncing' | 'success' | 'error'

/**
 * Whether the given sync status should trigger a beforeunload warning.
 * We warn when there are local changes that haven't reached the server yet.
 */
export function shouldWarnBeforeUnload(status: SyncStatus): boolean {
  return status === 'unsynced' || status === 'syncing'
}

/**
 * Check if note content is effectively empty (no visible text)
 */
export function isContentEmpty(content: string): boolean {
  if (!content) return true
  // Strip HTML tags and check for remaining text
  const plainText = content.replace(/<[^>]*>/g, '').trim()
  return !plainText
}

function lastPulledAtKey(userId: string): string {
  return `sync_last_pulled_at_${userId}`
}

/**
 * A note is dirty when it has local edits the server hasn't accepted yet.
 * Edits only bump updated_at; synced_at is stamped with the pushed snapshot's
 * updated_at, so this comparison is the single source of truth for dirtiness.
 */
export function isDirty(note: import('./dexie').Note): boolean {
  return !note.synced_at || new Date(note.updated_at) > new Date(note.synced_at)
}

/**
 * Whether a note is worth pushing. Blank notes are skipped only if they were
 * never synced — a blank note that has a synced_at is a note the user cleared,
 * and that deletion of content has to reach the server. Without the synced_at
 * exception such a note stays dirty forever: push skips it, and pull refuses
 * to overwrite dirty notes.
 */
function isPushable(note: import('./dexie').Note): boolean {
  return !isContentEmpty(note.content) || note.is_deleted || note.synced_at !== null
}

const REQUEST_TIMEOUT_MS = 15_000

/**
 * fetch() with a hard timeout that also honours a caller-supplied signal.
 *
 * Direct fetch is used instead of the Supabase client for notes because the
 * client's internal _getSession() middleware can hang intermittently and block
 * the request from ever reaching the network.
 */
async function fetchWithTimeout(url: string, init: RequestInit, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  if (signal) {
    if (signal.aborted) {
      controller.abort(signal.reason)
    } else {
      signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true })
    }
  }

  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timeoutId)
  }
}

interface UpsertConflict {
  id: string
  version: number
  server_updated_at: string
}

/**
 * Call the upsert_notes RPC and return whichever notes the server rejected
 * because its own version was greater than or equal to the pushed one.
 */
async function upsertNotesRpc(
  notes: unknown[],
  accessToken: string | undefined,
  signal?: AbortSignal,
): Promise<UpsertConflict[]> {
  if (!accessToken) {
    throw new Error('No auth session — cannot push notes')
  }

  const response = await fetchWithTimeout(`${supabaseRestUrl}/rpc/upsert_notes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'apikey': supabaseApiKey,
    },
    body: JSON.stringify({ notes }),
  }, signal)

  if (!response.ok) {
    const errorBody = await response.text()
    log.error('Push failed:', response.status, errorBody)
    throw new Error(`Failed to push notes: ${response.status} ${errorBody}`)
  }

  const conflicts = await response.json()
  return Array.isArray(conflicts) ? (conflicts as UpsertConflict[]) : []
}

/**
 * Resolve a version conflict in favour of the local copy.
 *
 * The device holding the local DB is the source of truth, so a conflict just
 * means the local version counter drifted below the server's. Lifting it above
 * the server's lets the next push through, which keeps a conflicted note from
 * being stranded as permanently dirty.
 */
async function adoptServerVersion(conflicts: UpsertConflict[]): Promise<void> {
  await Promise.all(
    conflicts.map(c => db.notes.update(String(c.id), { version: Number(c.version) + 1 }))
  )
}

/**
 * Full sync: pull then push.
 * Used on mount to catch up with any server state, then flush local changes.
 */
export async function sync(userId: string, signal?: AbortSignal, accessToken?: string): Promise<boolean> {
  if (!isSupabaseConfigured) {
    log.warn('Supabase not configured, skipping sync')
    return false
  }

  const syncStart = performance.now()
  log.warn('sync() ENTER — userId=%s, isSupabaseConfigured=%s', userId, isSupabaseConfigured)
  try {
    const lastPulledAt = localStorage.getItem(lastPulledAtKey(userId))
    log.warn('sync() calling pullRemoteNotes — lastPulledAt=%s', lastPulledAt ?? 'null')
    const maxServerUpdatedAt = await pullRemoteNotes(userId, signal, lastPulledAt, accessToken)
    // Use the server's own timestamp as the cursor for the next incremental
    // pull. This avoids client/server clock skew that could cause missed
    // updates. When results were returned, use the max server_updated_at.
    // When no results were returned but we have no cursor yet (first sync),
    // fall back to client time to bootstrap the cursor.
    if (maxServerUpdatedAt) {
      localStorage.setItem(lastPulledAtKey(userId), maxServerUpdatedAt)
    } else if (!lastPulledAt) {
      localStorage.setItem(lastPulledAtKey(userId), new Date().toISOString())
    }

    // Flush every dirty note, not just the one being edited. This is the only
    // path that recovers notes stranded by a failed push, a closed tab or an
    // offline session — without it they stay local until that same note is
    // edited again.
    signal?.throwIfAborted()
    await pushDirtyNotes(userId, signal, false, accessToken)
    signal?.throwIfAborted()
    await pushDirtyImages(userId, signal)

    const elapsed = (performance.now() - syncStart).toFixed(0)
    log.warn('sync() EXIT success — took %sms', elapsed)
    return true
  } catch (error) {
    const elapsed = (performance.now() - syncStart).toFixed(0)
    log.warn('sync() EXIT failure — took %sms, error:', elapsed, error)
    captureError(error, { source: 'sync.full', extra: { userId } })
    return false
  }
}

/**
 * Push-only sync: uploads local changes without pulling first.
 * Used on edit — the user just typed something, so the server has nothing
 * newer than what's in local DB. Skipping pull is faster and avoids the
 * risk of a stale server version overwriting an unpushed local edit.
 *
 * @param noteId  If provided, only push this specific note (auto_save).
 *                If omitted, push all dirty notes (fallback).
 */
export async function pushOnly(userId: string, signal?: AbortSignal, noteId?: string, accessToken?: string): Promise<boolean> {
  if (!isSupabaseConfigured) {
    log.warn('Supabase not configured, skipping push')
    return false
  }

  log.info('Starting push-only sync for user', userId, noteId ? `(note: ${noteId.slice(0, 8)})` : '(all dirty)')
  try {
    if (noteId) {
      await pushSingleNote(userId, noteId, signal, accessToken)
    } else {
      await pushDirtyNotes(userId, signal, false, accessToken)
    }
    signal?.throwIfAborted()
    await pushDirtyImages(userId, signal)
    log.info('Push-only sync completed successfully')
    return true
  } catch (error) {
    captureError(error, { source: 'sync.pushOnly', extra: { userId, noteId } })
    return false
  }
}

/**
 * Pull: Remote -> Local (merges cloud notes into local)
 * New notes from other devices are added
 * Existing notes are updated if remote is newer (last-write-wins)
 */
async function pullRemoteNotes(userId: string, signal?: AbortSignal, lastPulledAt?: string | null, cachedToken?: string): Promise<string | null> {
  // Direct fetch() to bypass the Supabase client's internal _getSession()
  // middleware which can hang intermittently, blocking the query from
  // ever reaching the network. Same approach used by pushSingleNote.
  const accessToken = cachedToken
  if (!accessToken) {
    throw new Error('No auth session — cannot pull notes')
  }

  // Build the query URL with PostgREST syntax
  let url = `${supabaseRestUrl}/notes?user_id=eq.${encodeURIComponent(userId)}&select=*`
  if (lastPulledAt) {
    url += `&server_updated_at=gt.${encodeURIComponent(lastPulledAt)}`
  }

  const response = await fetchWithTimeout(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'apikey': supabaseApiKey,
    },
  }, signal)

  if (!response.ok) {
    const errorBody = await response.text()
    log.error('Pull failed:', response.status, errorBody)
    throw new Error(`Failed to pull notes: ${response.status} ${errorBody}`)
  }

  type RemoteNote = import('./dexie').Note & { server_updated_at: string }
  const data: RemoteNote[] = await response.json()

  log.info('Pulled', (data ?? []).length, 'remote notes', lastPulledAt ? '(incremental)' : '(full)')

  const now = new Date().toISOString()
  const remotes = data ?? []

  if (remotes.length === 0) return null

  // Find the maximum server_updated_at from pulled results to use as a
  // reliable cursor for incremental pulls (avoids client/server clock skew).
  const maxServerUpdatedAt = remotes.reduce((max, r) => {
    return r.server_updated_at > max ? r.server_updated_at : max
  }, remotes[0].server_updated_at)

  // Batch fetch all local versions in one call instead of N+1 queries
  const localNotes = await db.notes.bulkGet(remotes.map(r => r.id))
  const localMap = new Map(
    localNotes
      .filter((n): n is import('./dexie').Note => n !== undefined)
      .map(n => [n.id, n])
  )

  const toPut: import('./dexie').Note[] = []
  for (const remote of remotes) {
    const local = localMap.get(remote.id)

    if (!local) {
      toPut.push({ ...remote, synced_at: now })
      continue
    }

    // Local edits always win. server_updated_at is stamped by the server at
    // write time while synced_at holds the client's updated_at from before the
    // push, so a just-pushed note always looks "newer" on the server. Without
    // this guard the pull would overwrite anything typed since that push and
    // stamp synced_at, marking the note clean so the loss is never recovered.
    if (isDirty(local)) {
      log.info('Skipping pull for locally modified note %s', remote.id.slice(0, 8))
      continue
    }

    if (new Date(remote.server_updated_at) > new Date(local.synced_at ?? '1970-01-01')) {
      toPut.push({ ...remote, synced_at: now })
    }
  }

  // Batch write all updates in one call
  if (toPut.length > 0) {
    await db.notes.bulkPut(toPut)
  }

  return maxServerUpdatedAt
}

/**
 * Push: Local -> Remote (uploads local changes)
 * Finds all "dirty" notes (never synced or modified since last sync)
 */
async function pushDirtyNotes(userId: string, signal?: AbortSignal, localOnly: boolean = false, accessToken?: string): Promise<void> {
  // Find dirty notes (never synced or modified since last sync).
  // When localOnly=true (auto_save), fetch only notes this tab modified
  // directly by ID — avoids scanning the entire table.
  // When localOnly=false (mount), scan all notes to catch up.
  // Skip blank notes — they shouldn't be pushed to the server.
  let candidates: import('./dexie').Note[]
  if (localOnly) {
    const ids = Array.from(locallyModifiedNoteIds)
    if (ids.length === 0) {
      log.info('No dirty notes to push')
      return
    }
    candidates = (await db.notes.bulkGet(ids)).filter((n): n is import('./dexie').Note => n !== undefined)
  } else {
    candidates = await db.notes.toArray()
  }

  const dirty = candidates.filter(n => isDirty(n) && isPushable(n))

  if (dirty.length === 0) {
    log.info('No dirty notes to push')
    return
  }

  log.info('Pushing', dirty.length, 'dirty notes')
  log.info('Dirty notes:', dirty.map(n => ({
    id: n.id.slice(0, 8),
    version: n.version,
    updated_at: n.updated_at,
    synced_at: n.synced_at,
  })))

  // Bump versions right before pushing — version only increments on sync,
  // not on every keystroke, to avoid inflation.
  await Promise.all(
    dirty.map(n => db.notes.update(n.id, { version: (n.version ?? 1) + 1 }))
  )

  const notesToUpsert = dirty.map(n => ({
    id: n.id,
    content: n.content,
    custom_title: n.custom_title ?? null,
    created_at: n.created_at,
    updated_at: n.updated_at,
    is_deleted: n.is_deleted,
    is_pinned: n.is_pinned,
    user_id: userId,
    version: (n.version ?? 1) + 1,
  }))

  log.info('Calling upsert_notes RPC...')

  const conflicts = await upsertNotesRpc(notesToUpsert, accessToken, signal)
  log.info('upsert_notes RPC returned, conflicts:', conflicts)

  const conflictIds = new Set<string>()
  if (conflicts.length > 0) {
    log.warn('Version conflicts for', conflicts.length, 'notes:', conflicts)
    for (const c of conflicts) {
      conflictIds.add(String(c.id))
    }
    // Lift the local versions above the server's so the next flush wins.
    // These notes stay dirty and are retried on the following sync.
    await adoptServerVersion(conflicts)
  }

  // Mark as synced locally. We set synced_at to the snapshot's updated_at
  // (not "now") so that if the user typed more while the push was in flight,
  // those newer edits (with a later updated_at) will still be detected as
  // dirty on the next cycle.
  const toMark = dirty.filter(n => !conflictIds.has(n.id))
  log.info('Marking', toMark.length, 'notes as synced')
  await Promise.all(
    toMark.map(n => {
      log.info('Setting synced_at=%s for note %s', n.updated_at, n.id.slice(0, 8))
      return db.notes.update(n.id, { synced_at: n.updated_at, user_id: userId })
    })
  )

  // Clear locally modified tracking for successfully synced notes
  for (const n of toMark) {
    locallyModifiedNoteIds.delete(n.id)
  }

  // Clean up server-side images for any deleted notes that were successfully synced
  const deletedNotes = toMark.filter(n => n.is_deleted)
  for (const n of deletedNotes) {
    await cleanupServerImagesForNote(userId, n.id)
  }
}

/**
 * Push a single note by ID. Used by auto_save — the tab knows exactly
 * which note it just edited, no need to scan the DB.
 */
async function pushSingleNote(userId: string, noteId: string, signal?: AbortSignal, cachedToken?: string, isRetry = false): Promise<void> {
  const note = await db.notes.get(noteId)
  if (!note) {
    log.info('Note not found, skipping push:', noteId.slice(0, 8))
    return
  }

  // Check if it's actually dirty
  if (!isDirty(note)) {
    log.info('Note already synced, skipping:', noteId.slice(0, 8))
    return
  }

  // Skip blank notes that were never synced — a cleared note still has to push
  if (!isPushable(note)) {
    log.info('Note is blank and never synced, skipping push:', noteId.slice(0, 8))
    return
  }

  // Bump version right before pushing — version only increments on sync,
  // not on every keystroke, to avoid inflation.
  const newVersion = (note.version ?? 1) + 1
  await db.notes.update(noteId, { version: newVersion })

  log.info('Pushing note:', noteId.slice(0, 8), 'version:', newVersion, 'updated_at:', note.updated_at)

  const noteToUpsert = {
    id: note.id,
    content: note.content,
    custom_title: note.custom_title ?? null,
    created_at: note.created_at,
    updated_at: note.updated_at,
    is_deleted: note.is_deleted,
    is_pinned: note.is_pinned,
    user_id: userId,
    version: newVersion,
  }

  log.info('Calling upsert_notes via direct fetch...')
  const conflicts = await upsertNotesRpc([noteToUpsert], cachedToken, signal)
  log.info('upsert_notes returned, conflicts:', conflicts)

  if (conflicts.length > 0) {
    log.warn('Version conflict for note:', noteId.slice(0, 8), conflicts)
    await adoptServerVersion(conflicts)
    if (isRetry) {
      // Still conflicting after adopting the server's version — leave it dirty
      // rather than looping. The next full sync retries it.
      log.warn('Conflict persisted after retry for note:', noteId.slice(0, 8))
      return
    }
    return pushSingleNote(userId, noteId, signal, cachedToken, true)
  }

  // Mark as synced
  log.info('Setting synced_at=%s for note %s', note.updated_at, noteId.slice(0, 8))
  await db.notes.update(noteId, { synced_at: note.updated_at, user_id: userId })
  locallyModifiedNoteIds.delete(noteId)

  // Clean up server-side images for deleted notes
  if (note.is_deleted) {
    await cleanupServerImagesForNote(userId, noteId)
    return
  }

  // Push any unsynced images for this note now that the note exists on the server
  await pushImagesForNote(userId, noteId, signal)
}

const IMAGE_BUCKET = 'note-images'

/**
 * Clean up server-side images for a deleted note.
 * Removes image metadata from the `images` table and files from Supabase Storage.
 * Called after a soft-deleted note is successfully pushed to the server.
 * Failures are logged but non-fatal — cleanup issues shouldn't break sync.
 */
async function cleanupServerImagesForNote(userId: string, noteId: string): Promise<void> {
  try {
    // Fetch storage paths before deleting metadata (needed for storage cleanup)
    const { data: imageRecords, error: fetchError } = await supabase
      .from('images')
      .select('storage_path')
      .eq('note_id', noteId)
      .eq('user_id', userId)

    if (fetchError) {
      captureError(new Error(fetchError.message), { source: 'sync.cleanupImages.fetch', extra: { noteId, userId } })
      return
    }

    const storagePaths: string[] = (imageRecords ?? []).map(r => r.storage_path).filter(Boolean)

    if (storagePaths.length === 0) {
      log.info('No server images to clean up for deleted note:', noteId.slice(0, 8))
      return
    }

    // Delete image metadata from the server
    const { error: deleteError } = await supabase
      .from('images')
      .delete()
      .eq('note_id', noteId)
      .eq('user_id', userId)

    if (deleteError) {
      captureError(new Error(deleteError.message), { source: 'sync.cleanupImages.deleteMeta', extra: { noteId, userId } })
      return
    }

    log.info('Deleted %d image metadata rows for note %s', storagePaths.length, noteId.slice(0, 8))

    // Fire-and-forget: delete image files from Supabase Storage
    supabase.storage
      .from(IMAGE_BUCKET)
      .remove(storagePaths)
      .then(({ error }) => {
        if (error) {
          captureError(new Error(error.message), { source: 'sync.cleanupImages.storage', extra: { noteId, count: storagePaths.length } })
        } else {
          log.info('Cleaned up %d image files from storage for note %s', storagePaths.length, noteId.slice(0, 8))
        }
      })
  } catch (error) {
    captureError(error, { source: 'sync.cleanupImages', extra: { noteId, userId } })
  }
}

/**
 * Push images for a specific note to Supabase Storage + metadata table.
 * Called right after a note is successfully pushed, so the FK constraint
 * on note_id is guaranteed to be satisfied.
 */
async function pushImagesForNote(userId: string, noteId: string, signal?: AbortSignal): Promise<void> {
  const noteImages = await db.images.where('note_id').equals(noteId).toArray()
  const dirty = noteImages.filter(img => !img.synced_at)

  if (dirty.length === 0) return

  log.info('Pushing', dirty.length, 'images for note', noteId.slice(0, 8))

  const CONCURRENCY = 3
  for (let i = 0; i < dirty.length; i += CONCURRENCY) {
    signal?.throwIfAborted()
    const batch = dirty.slice(i, i + CONCURRENCY)

    await Promise.all(batch.map(async (img) => {
      await uploadAndSyncImage(img, userId)
    }))
  }
}

/**
 * Upload a single image to Supabase Storage and insert its metadata row.
 * Shared by pushImagesForNote and pushDirtyImages.
 */
async function uploadAndSyncImage(img: import('./dexie').ImageRecord, userId: string): Promise<void> {
  const storagePath = `${userId}/${img.id}`
  const imgShort = img.id.slice(0, 8)
  const t0 = performance.now()

  log.info('[ImageUpload] START %s (note: %s, size: %d bytes, type: %s)', imgShort, img.note_id.slice(0, 8), img.size, img.mime_type)

  log.info('[ImageUpload] %s — calling storage.upload...', imgShort)
  const t1 = performance.now()
  const { error: uploadError } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(storagePath, img.data, {
      contentType: img.mime_type,
      upsert: true,
    })
  const t2 = performance.now()
  log.info('[ImageUpload] %s — storage.upload returned in %dms, error: %s', imgShort, (t2 - t1).toFixed(0), uploadError?.message ?? 'none')

  if (uploadError) {
    captureError(new Error(uploadError.message), { source: 'sync.imageUpload', extra: { imageId: img.id, noteId: img.note_id } })
    return
  }

  log.info('[ImageUpload] %s — calling metadata upsert...', imgShort)
  const t3 = performance.now()
  const { error: metaError } = await supabase
    .from('images')
    .upsert({
      id: img.id,
      note_id: img.note_id,
      user_id: userId,
      mime_type: img.mime_type,
      size: img.size,
      storage_path: storagePath,
      created_at: img.created_at,
    }, { onConflict: 'id' })
  const t4 = performance.now()
  log.info('[ImageUpload] %s — metadata upsert returned in %dms, error: %s', imgShort, (t4 - t3).toFixed(0), metaError?.message ?? 'none')

  if (metaError) {
    captureError(new Error(metaError.message), { source: 'sync.imageMetadata', extra: { imageId: img.id, noteId: img.note_id } })
    return
  }

  const now = new Date().toISOString()
  await db.images.update(img.id, { synced_at: now, user_id: userId })
  log.info('[ImageUpload] DONE %s — total %dms', imgShort, (performance.now() - t0).toFixed(0))
}

/**
 * Push: Local images -> Supabase Storage + metadata table
 * Catch-all for any images not yet synced (e.g. from previous sessions).
 * Images whose parent notes haven't been synced yet are skipped.
 */
async function pushDirtyImages(userId: string, signal?: AbortSignal): Promise<void> {
  const allImages = await db.images.toArray()
  const unsyncedImages = allImages.filter(img => !img.synced_at)

  if (unsyncedImages.length === 0) {
    log.info('No dirty images to push')
    return
  }

  // Only push images whose parent notes have already been synced to the server.
  // The images table has a FK constraint on note_id → notes(id), so inserting
  // image metadata before the note exists on the server causes a 409 conflict.
  const noteIds = [...new Set(unsyncedImages.map(img => img.note_id))]
  const parentNotes = await db.notes.bulkGet(noteIds)
  const syncedNoteIds = new Set(
    parentNotes
      .filter((n): n is import('./dexie').Note => n !== undefined && n.synced_at !== null)
      .map(n => n.id)
  )

  const dirty = unsyncedImages.filter(img => syncedNoteIds.has(img.note_id))

  if (dirty.length === 0) {
    log.info('No dirty images with synced parent notes to push (%d images waiting for note sync)', unsyncedImages.length)
    return
  }

  log.info('Pushing', dirty.length, 'dirty images (%d waiting for note sync)', unsyncedImages.length - dirty.length)

  // Upload images with limited concurrency (3 at a time) to avoid overwhelming the network
  const CONCURRENCY = 3
  for (let i = 0; i < dirty.length; i += CONCURRENCY) {
    signal?.throwIfAborted()
    const batch = dirty.slice(i, i + CONCURRENCY)

    await Promise.all(batch.map(img => uploadAndSyncImage(img, userId)))
  }
}

/**
 * Get count of unsynced notes
 */
export async function getUnsyncedCount(): Promise<number> {
  return db.notes.filter(n => isDirty(n)).count()
}
